import * as React from 'react';
import { Routes, Route, Navigate, Outlet } from "react-router-dom";
import LoadingFallback from './LoadingFallback';
import { RootRedirect } from './RootRedirect';
import RouteErrorBoundary from '@/components/app/RouteErrorBoundary';
import { isAgentWithLimitedAccess } from '@/utils/navigationPermissions';
import Protect from '../auth/Protect';

// Import directly instead of lazy loading for critical components
import DashboardLayout from '@/layouts/DashboardLayout';
import AllContacts from '@/pages/contacts/All';
import Customers from '@/pages/contacts/Customers';
import Visitors from '@/pages/contacts/Visitors';

// Import inbox routes
import { createAndSubscribeToChannel, cleanupAbly, initAbly } from '@/utils/ably';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { getUserNotifications } from '@/store/slices/notificationSlice';
// Import automation and settings components
import Automation from '@/pages/automation';
import Settings from '@/pages/settings/Settings';
import ActionDetail from '@/pages/automation/ActionDetail';
import ToolsPage from '@/pages/automation/ToolsPage';
import AutoQA from '@/pages/settings/AutoQA';
import ScoringAgentDetail from '@/pages/settings/ScoringAgentDetail';


// Lazy load non-critical pages
const ForgotPassword = React.lazy(() => import('@/pages/ForgotPassword'));
const ResetPassword = React.lazy(() => import('@/pages/ResetPassword'));
const SignUp = React.lazy(() => import('@/pages/SignUp'));
const NotFound = React.lazy(() => import('@/pages/NotFound'));
const LandingPage = React.lazy(() => import('@/pages/LandingPage'));
const Companies = React.lazy(() => import('@/pages/contacts/Companies'));
const CompanyDetail = React.lazy(() => import('@/pages/contacts/CompanyDetail'));
const ContactDetail = React.lazy(() => import('@/pages/contacts/Detail'));

// Inbox components
const YourInbox = React.lazy(() => import('@/pages/inbox/YourInbox'));
const MentionsInbox = React.lazy(() => import('@/pages/inbox/Mentions'));
const AllTickets = React.lazy(() => import('@/pages/inbox/All'));
const UnassignedInbox = React.lazy(() => import('@/pages/inbox/Unassigned'));
const TeamList = React.lazy(() => import('@/pages/inbox/TeamList'));
const TeamView = React.lazy(() => import('@/pages/inbox/TeamView'));
const TeammatePage = React.lazy(() => import('@/pages/inbox/TeammatePage'));
const BotView = React.lazy(() => import('@/pages/Inbox/BotView'));

// Automation components
const WorkflowsPage = React.lazy(() => import('@/pages/automation/WorkflowsPage'));
const WorkflowBuilderPage = React.lazy(() => import('@/pages/automation/WorkflowBuilderPage'));
const ContentCenter = React.lazy(() => import('@/pages/automation/ContentCenter'));
const ContentDetail = React.lazy(() => import('@/pages/automation/ContentDetail'));
const ActionCenter = React.lazy(() => import('@/pages/automation/ActionCenter'));
const CreateAction = React.lazy(() => import('@/pages/automation/CreateAction'));
const ChatbotProfiles = React.lazy(() => import('@/pages/automation/ChatbotProfiles'));
const ChatbotDetail = React.lazy(() => import('@/pages/automation/ChatbotDetail'));
const CreateChatbot = React.lazy(() => import('@/pages/automation/CreateChatbot'));
const EditChatbot = React.lazy(() => import('@/pages/automation/EditChatbot'));
const ChatbotSettings = React.lazy(() => import('@/pages/automation/ChatbotSettings'));
const CopilotList = React.lazy(() => import('@/pages/automation/copilot/CopilotList'));
const CopilotCreate = React.lazy(() => import('@/pages/automation/copilot/CopilotCreate'));
const CopilotEdit = React.lazy(() => import('@/pages/automation/copilot/CopilotEdit'));
const BotInbox = React.lazy(() => import('@/pages/inbox/BotInbox'));

// Settings components
const Chat = React.lazy(() => import('@/pages/settings/Chat'));
const GeneralSettings = React.lazy(() => import('@/pages/settings/GeneralSettings'));
const TicketSettings = React.lazy(() => import('@/pages/settings/TicketSettings'));
const CustomObjects = React.lazy(() => import('@/pages/settings/CustomObjects'));
const EmailDomainsSettings = React.lazy(() => import('@/pages/settings/email/domains/Domains'));
const EmailDomainDetail = React.lazy(() => import('@/pages/settings/email/domain-detail/DomainDetail'));
const EmailChannels = React.lazy(() => import('@/pages/settings/email/channels/Channels'));
const CreateChannel = React.lazy(() => import('@/pages/settings/email/channels/CreateChannel'));
const EditChannel = React.lazy(() => import('@/pages/settings/email/channels/EditChannel'));
const Tags = React.lazy(() => import('@/pages/settings/Tags'));
const CustomData = React.lazy(() => import('@/pages/settings/CustomData'));
const CustomObjectDetail = React.lazy(() => import('@/pages/settings/CustomObjectDetail'));
const Teammates = React.lazy(() => import('@/pages/settings/Teammates'));
const TeammateDetail = React.lazy(() => import('@/pages/settings/teammates/TeammateDetail'));
const Teams = React.lazy(() => import('@/pages/settings/Teams'));
const TeamDetail = React.lazy(() => import('@/pages/settings/TeamDetail'));
const EditTeam = React.lazy(() => import('@/pages/settings/EditTeam'));
const CannedResponses = React.lazy(() => import('@/pages/settings/CannedResponses'));
const CreateCannedResponse = React.lazy(() => import('@/pages/settings/CreateCannedResponse'));
const CannedResponseDetail = React.lazy(() => import('@/pages/settings/CannedResponseDetail'));
const CreateTeam = React.lazy(() => import('@/pages/settings/CreateTeam'));
const AutoReply = React.lazy(() => import('@/pages/settings/AutoReply'));
const AutoReplyTemp = React.lazy(() => import('@/pages/settings/AutoReplyTemp'));
const EmailTemplates = React.lazy(() => import('@/pages/settings/email-templates/index'));

// AutoQA components
const SupervisorCockpit = React.lazy(() => import('@/pages/settings/autoqa/SupervisorCockpit'));
const MyScorecards = React.lazy(() => import('@/pages/autoqa/MyScorecards'));
const ScorecardDetail = React.lazy(() => import('@/pages/autoqa/ScorecardDetail'));
const TeammateScoringAgents = React.lazy(() => import('@/pages/settings/autoqa/TeammateScoringAgents'));
const TeamPerformance = React.lazy(() => import('@/pages/settings/autoqa/TeamPerformance'));
const TeammateScorecard = React.lazy(() => import('@/pages/settings/autoqa/TeammateScorecard'));

// Reporting components
const TicketAnalytics = React.lazy(() => import('@/pages/Reporting/TicketAnalytics'));
const AgentPerformance = React.lazy(() => import('@/pages/Reporting/AgentPerformance'));
const CustomerInsights = React.lazy(() => import('@/pages/Reporting/CustomerInsights'));
const ChannelAnalytics = React.lazy(() => import('@/pages/Reporting/ChannelAnalytics'));
const SLAReports = React.lazy(() => import('@/pages/Reporting/SLAReports'));
// const CustomReports = React.lazy(() => import('@/pages/Reporting/CustomReports'));
const ReportingDashboard = React.lazy(() => import('@/pages/Reporting/Dashboard'));
const PredefinedReports = React.lazy(() => import('@/pages/Reporting/PredefinedReports'));

// Individual predefined report pages
const TicketAnalyticsReport = React.lazy(() => import('@/pages/Reporting/TicketAnalytics'));
const EffectivenessReport = React.lazy(() => import('@/pages/Reporting/Effectiveness'));
const AIChatbotsReport = React.lazy(() => import('@/pages/Reporting/AIChatbots'));
const AICopilotReport = React.lazy(() => import('@/pages/Reporting/AICopilot'));
const ResponsivenessReport = React.lazy(() => import('@/pages/Reporting/Responsiveness'));
const CSATReport = React.lazy(() => import('@/pages/Reporting/CSAT'));
const TeamPerformanceReport = React.lazy(() => import('@/pages/Reporting/TeamPerformance'));
const TeammatePerformanceReport = React.lazy(() => import('@/pages/Reporting/TeammatePerformance'));
const AcceptInvitationPage = React.lazy(() => import('@/pages/AcceptInvitation'));
const SignIn = React.lazy(() => import('@/pages/SignIn'));

// 🎯 Agent Redirect Component
const AgentInboxRedirect = () => {
  if (isAgentWithLimitedAccess()) {
    return <Navigate to="/home/inbox/your-inbox" replace />;
  }
  return <Navigate to="/home/inbox/all" replace />;
};

import SetPassword from '@/pages/SetPassword';
import { getPermissionForNavigation } from '@/utils/betterAuthPermissionMapper';
import { AuthenticatedRoutesWrapper } from '@/wrappers/AuthenticatedRoutesWrapper';

/**
 * Main routing component for the application
 * Uses React Router v6 for declarative routing
 */
const AppRoutes: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const dispatch = useAppDispatch();
  const notifications = useSelector((state: RootState) => state.notification);
  const [lastUserKey, setLastUserKey] = React.useState<string | null>(null);

  useEffect(() => {
    const userKey = user?.id && user?.clientId ? `${user.id}-${user.clientId}` : null;

    // Only initialize if user key changed
    if (userKey && userKey !== lastUserKey) {
      setLastUserKey(userKey);
      initAbly();
      dispatch(getUserNotifications());
      createAndSubscribeToChannel(user.id, user.clientId, dispatch, notifications);
    }

    // Cleanup function to unsubscribe from Ably channels when component unmounts or user changes
    return () => {
      cleanupAbly();
    };
  }, [user?.id, user?.clientId, dispatch, lastUserKey]); // Track lastUserKey to prevent re-initialization

  return (
    <React.Suspense fallback={<LoadingFallback />}>
      <Routes>
        {/* Public routes  */}
        <Route path="/accept-invitation/:id" element={
          <React.Suspense fallback={<LoadingFallback />}>
            <AcceptInvitationPage />
          </React.Suspense>
        } />

        <Route path="sign-in" element={
          <React.Suspense fallback={<LoadingFallback />}>
            <SignIn />
          </React.Suspense>
        } />



        {/*Authenticated Routes*/}
        <Route element={<AuthenticatedRoutesWrapper />}>
          {/* Public routes */}
          <Route path="/" element={
            <React.Suspense fallback={<LoadingFallback />}>
              <Navigate to="/home/inbox/all" replace />;
            </React.Suspense>
          } />
          <Route path="/home" element={<RootRedirect />} />
          <Route path="/forgot-password" element={
            <React.Suspense fallback={<LoadingFallback />}>
              <ForgotPassword />
            </React.Suspense>
          } />
          <Route path="/reset-password" element={
            <React.Suspense fallback={<LoadingFallback />}>
              <ResetPassword />
            </React.Suspense>
          } />
          {/*
          SIGN-UP ROUTE DISABLED:
          The sign-up route is intentionally commented out. All attempts to access sign-up should fallback to /sign-in.
          To re-enable, uncomment the code below and ensure sign-up is allowed in the backend.

          <Route path="/sign-up" element={
            <React.Suspense fallback={<LoadingFallback />}>
              <SignUp />
            </React.Suspense>
          } />
        */}
          <Route path="/sign-up" element={<Navigate to="/sign-in" replace />} />
          <Route path="/set-password" element={<SetPassword />} />

          {/* Protected routes */}
          <Route path="/home" element={
            // <ProtectedRoute>
            <DashboardLayout />
            // </ProtectedRoute>
          }>
            {/* 🎯 Default redirect based on user role */}
            <Route index element={<AgentInboxRedirect />} />

            {/* 🎯 Inbox routes - using Clerk Protect */}
            <Route path="inbox" element={<Outlet />}>
              <Route index element={<AgentInboxRedirect />} />

              <Route path="your-inbox" element={
                <Protect permission={getPermissionForNavigation('inbox.your-inbox')} fallback={<Navigate to="/home/inbox/your-inbox" replace />}>
                  <React.Suspense fallback={<LoadingFallback />}>
                    <YourInbox />
                  </React.Suspense>
                </Protect>
              } />

              <Route path="mentions" element={
                <Protect permission={getPermissionForNavigation('inbox.mentions')} fallback={<Navigate to="/home/inbox/your-inbox" replace />}>
                  <React.Suspense fallback={<LoadingFallback />}>
                    <MentionsInbox />
                  </React.Suspense>
                </Protect>
              } />

              <Route
                path="all"
                element={
                  <Protect permission={getPermissionForNavigation('inbox.all')} fallback={<Navigate to="/home/inbox/your-inbox" replace />}>
                    <React.Suspense fallback={<LoadingFallback />}>
                      <div className="overflow-hidden">
                        <AllTickets />
                      </div>
                    </React.Suspense>
                  </Protect>
                }
              />

              <Route path="unassigned" element={
                <Protect permission={getPermissionForNavigation('inbox.unassigned')} fallback={<Navigate to="/home/inbox/your-inbox" replace />}>
                  <React.Suspense fallback={<LoadingFallback />}>
                    <UnassignedInbox />
                  </React.Suspense>
                </Protect>
              } />

              <Route path="teams" element={
                <React.Suspense fallback={<LoadingFallback />}>
                  <TeamList />
                </React.Suspense>
              } />

              <Route path="teams/:teamId" element={
                <React.Suspense fallback={<LoadingFallback />}>
                  <TeamView />
                </React.Suspense>
              } />

              <Route path="bot" element={
                <React.Suspense fallback={<LoadingFallback />}>
                  <BotInbox />
                </React.Suspense>
              } />

              <Route path="bot/:botId" element={
                <React.Suspense fallback={<LoadingFallback />}>
                  <BotView />
                </React.Suspense>
              } />

              <Route path="teammates/:teammateId" element={
                <React.Suspense fallback={<LoadingFallback />}>
                  <TeammatePage />
                </React.Suspense>
              } />
            </Route>

            {/* 🎯 Contacts routes - using Clerk Protect */}
            <Route path="contacts/*" element={
              <Protect permission={getPermissionForNavigation('contacts')} fallback={<Navigate to="/home/inbox/your-inbox" replace />}>
                <Outlet />
              </Protect>
            }>
              <Route index element={<Navigate to="all" replace />} />
              <Route path="all" element={<AllContacts />} />
              <Route path="customers" element={<Customers />} />
              <Route path="visitors" element={<Visitors />} />
              <Route path="companies" element={
                <React.Suspense fallback={<LoadingFallback />}>
                  <Companies />
                </React.Suspense>
              } />
              <Route path="companies/:id" element={
                <React.Suspense fallback={<LoadingFallback />}>
                  <CompanyDetail />
                </React.Suspense>
              } />
              <Route path=":id" element={
                <React.Suspense fallback={<LoadingFallback />}>
                  <ContactDetail />
                </React.Suspense>
              } />
            </Route>

            {/* 🎯 Automation routes - using Clerk Protect */}
            <Route path="automation/*" element={
              <Protect permission={getPermissionForNavigation('automation')} fallback={<Navigate to="/home/inbox/your-inbox" replace />}>
                <RouteErrorBoundary>
                  <Automation />
                </RouteErrorBoundary>
              </Protect>
            }>
              <Route index element={<Navigate to="workflows" replace />} />

              <Route path="workflows" element={
                <React.Suspense fallback={<LoadingFallback />}>
                  <WorkflowsPage />
                </React.Suspense>
              } />

              <Route path="workflows/new/:workflowId" element={
                <React.Suspense fallback={<LoadingFallback />}>
                  <WorkflowBuilderPage />
                </React.Suspense>
              } />

              <Route path="workflows/edit/:workflowId" element={
                <React.Suspense fallback={<LoadingFallback />}>
                  <WorkflowBuilderPage />
                </React.Suspense>
              } />

              <Route path="ai/content-center" element={
                <React.Suspense fallback={<LoadingFallback />}>
                  <ContentCenter />
                </React.Suspense>
              } />

              <Route path="ai/content-center/:id" element={
                <React.Suspense fallback={<LoadingFallback />}>
                  <ContentDetail />
                </React.Suspense>
              } />

              <Route path="ai/action-center" element={
                <React.Suspense fallback={<LoadingFallback />}>
                  <ActionCenter />
                </React.Suspense>
              } />

              <Route path="ai/action-center/:id" element={
                <React.Suspense fallback={<LoadingFallback />}>
                  <ActionDetail />
                </React.Suspense>
              } />

              <Route path="ai/action-center/create" element={
                <React.Suspense fallback={<LoadingFallback />}>
                  <CreateAction />
                </React.Suspense>
              } />

              <Route path="ai/chatbot-profiles" element={
                <React.Suspense fallback={<LoadingFallback />}>
                  <ChatbotProfiles />
                </React.Suspense>
              } />

              <Route path="ai/chatbot-profiles/create" element={
                <React.Suspense fallback={<LoadingFallback />}>
                  <CreateChatbot />
                </React.Suspense>
              } />

              <Route path="ai/chatbot-profiles/:id" element={
                <React.Suspense fallback={<LoadingFallback />}>
                  <ChatbotDetail />
                </React.Suspense>
              } />

              <Route path="ai/chatbot-profiles/:id/edit" element={
                <React.Suspense fallback={<LoadingFallback />}>
                  <EditChatbot />
                </React.Suspense>
              } />

              <Route path="ai/chatbot-profiles/:id/settings" element={
                <React.Suspense fallback={<LoadingFallback />}>
                  <ChatbotSettings />
                </React.Suspense>
              } />

              <Route path="ai/copilot" element={
                <React.Suspense fallback={<LoadingFallback />}>
                  <CopilotList />
                </React.Suspense>
              } />

              <Route path="ai/copilot/create" element={
                <React.Suspense fallback={<LoadingFallback />}>
                  <CopilotCreate />
                </React.Suspense>
              } />

              <Route path="ai/copilot/:copilotId/edit" element={
                <React.Suspense fallback={<LoadingFallback />}>
                  <CopilotEdit />
                </React.Suspense>
              } />

              <Route path="ai/bot-inboxes" element={
                <React.Suspense fallback={<LoadingFallback />}>
                  <BotInbox />
                </React.Suspense>
              } />

              <Route path="ai/bot-inboxes/:id" element={
                <React.Suspense fallback={<LoadingFallback />}>
                  <BotInbox />
                </React.Suspense>
              } />

              <Route path="ai/tools" element={
                <React.Suspense fallback={<LoadingFallback />}>
                  <ToolsPage />
                </React.Suspense>
              } />
            </Route>

            {/* 🎯 AutoQA routes - using Clerk Protect */}
            <Route path="autoqa/*" element={
              <Protect permission={getPermissionForNavigation('settings.autoqa')} fallback={<Navigate to="/home/inbox/your-inbox" replace />}>
                <RouteErrorBoundary>
                  <Outlet />
                </RouteErrorBoundary>
              </Protect>
            }>
              <Route index element={<Navigate to="supervisor-cockpit" replace />} />

              <Route path="supervisor-cockpit" element={
                <React.Suspense fallback={<LoadingFallback />}>
                  <SupervisorCockpit />
                </React.Suspense>
              } />

              <Route path="my-scorecards" element={
                <React.Suspense fallback={<LoadingFallback />}>
                  <MyScorecards />
                </React.Suspense>
              } />

              <Route path="my-scorecards/:id" element={
                <React.Suspense fallback={<LoadingFallback />}>
                  <ScorecardDetail />
                </React.Suspense>
              } />

              <Route path="teammate-scorecards" element={
                <React.Suspense fallback={<LoadingFallback />}>
                  <TeammateScoringAgents />
                </React.Suspense>
              } />

              <Route path="teammate-scorecards/:teammateId" element={
                <React.Suspense fallback={<LoadingFallback />}>
                  <TeammateScorecard />
                </React.Suspense>
              } />

              <Route path="team-performance" element={
                <React.Suspense fallback={<LoadingFallback />}>
                  <TeamPerformance />
                </React.Suspense>
              } />
            </Route>

            {/* 🎯 Reporting routes - using Clerk Protect */}
            <Route path="reporting" element={
              <Protect permission={getPermissionForNavigation('reporting')} fallback={<Navigate to="/home/inbox/your-inbox" replace />}>
                <Outlet />
              </Protect>
            }>
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={
                <React.Suspense fallback={<LoadingFallback />}>
                  <ReportingDashboard />
                </React.Suspense>
              } />
              <Route path="tickets" element={
                <React.Suspense fallback={<LoadingFallback />}>
                  <TicketAnalytics />
                </React.Suspense>
              } />
              <Route path="agents" element={
                <React.Suspense fallback={<LoadingFallback />}>
                  <AgentPerformance />
                </React.Suspense>
              } />
              <Route path="customers" element={
                <React.Suspense fallback={<LoadingFallback />}>
                  <CustomerInsights />
                </React.Suspense>
              } />
              <Route path="channels" element={
                <React.Suspense fallback={<LoadingFallback />}>
                  <ChannelAnalytics />
                </React.Suspense>
              } />
              <Route path="sla" element={
                <React.Suspense fallback={<LoadingFallback />}>
                  <SLAReports />
                </React.Suspense>
              } />
              {/* <Route path="custom" element={
              <React.Suspense fallback={<LoadingFallback />}>
                <CustomReports />
              </React.Suspense>
            } /> */}
              <Route path="reports" element={<Outlet />}>
                <Route index element={
                  <React.Suspense fallback={<LoadingFallback />}>
                    <PredefinedReports />
                  </React.Suspense>
                } />
              </Route>

              {/* Individual predefined report routes - not in navigation */}
              <Route path="ticket-analytics" element={
                <React.Suspense fallback={<LoadingFallback />}>
                  <TicketAnalyticsReport />
                </React.Suspense>
              } />
              <Route path="effectiveness" element={
                <React.Suspense fallback={<LoadingFallback />}>
                  <EffectivenessReport />
                </React.Suspense>
              } />
              <Route path="ai-chatbots" element={
                <React.Suspense fallback={<LoadingFallback />}>
                  <AIChatbotsReport />
                </React.Suspense>
              } />
              <Route path="ai-copilot" element={
                <React.Suspense fallback={<LoadingFallback />}>
                  <AICopilotReport />
                </React.Suspense>
              } />
              <Route path="responsiveness" element={
                <React.Suspense fallback={<LoadingFallback />}>
                  <ResponsivenessReport />
                </React.Suspense>
              } />
              <Route path="csat" element={
                <React.Suspense fallback={<LoadingFallback />}>
                  <CSATReport />
                </React.Suspense>
              } />
              <Route path="team-performance" element={
                <React.Suspense fallback={<LoadingFallback />}>
                  <TeamPerformanceReport />
                </React.Suspense>
              } />
              <Route path="teammate-performance" element={
                <React.Suspense fallback={<LoadingFallback />}>
                  <TeammatePerformanceReport />
                </React.Suspense>
              } />
            </Route>

            {/* 🎯 Settings routes - using Clerk Protect */}
            <Route path="settings/*" element={
              <Protect permission={getPermissionForNavigation('settings')} fallback={<Navigate to="/home/inbox/your-inbox" replace />}>
                <RouteErrorBoundary>
                  <Settings />
                </RouteErrorBoundary>
              </Protect>
            }>
              <Route index element={
                <Protect permission={getPermissionForNavigation('settings.general')} fallback={<Navigate to="/home/settings/tags" replace />}>
                  <Navigate to="general" replace />
                </Protect>
              } />

              <Route path="general" element={
                <Protect permission={getPermissionForNavigation('settings.general')} fallback={<Navigate to="/home/settings/tags" replace />}>
                  <React.Suspense fallback={<LoadingFallback />}>
                    <GeneralSettings />
                  </React.Suspense>
                </Protect>
              } />

              <Route path="tickets" element={
                <Protect permission={getPermissionForNavigation('settings.tickets')} fallback={<Navigate to="/home/settings/tags" replace />}>
                  <React.Suspense fallback={<LoadingFallback />}>
                    <TicketSettings />
                  </React.Suspense>
                </Protect>
              } />

              <Route path="email/domains" element={
                <Protect permission={getPermissionForNavigation('settings.email')} fallback={<Navigate to="/home/settings/tags" replace />}>
                  <React.Suspense fallback={<LoadingFallback />}>
                    <EmailDomainsSettings />
                  </React.Suspense>
                </Protect>
              } />

              <Route path="email/domains/:id" element={
                <Protect permission={getPermissionForNavigation('settings.email')} fallback={<Navigate to="/home/settings/tags" replace />}>
                  <React.Suspense fallback={<LoadingFallback />}>
                    <EmailDomainDetail />
                  </React.Suspense>
                </Protect>
              } />

              <Route path="email/channels" element={
                <Protect permission={getPermissionForNavigation('settings.email')} fallback={<Navigate to="/home/settings/tags" replace />}>
                  <React.Suspense fallback={<LoadingFallback />}>
                    <EmailChannels />
                  </React.Suspense>
                </Protect>
              } />

              <Route path="email/channels/create" element={
                <Protect permission={getPermissionForNavigation('settings.email')} fallback={<Navigate to="/home/settings/tags" replace />}>
                  <React.Suspense fallback={<LoadingFallback />}>
                    <CreateChannel />
                  </React.Suspense>
                </Protect>
              } />

              <Route path="email/channels/:id" element={
                <Protect permission={getPermissionForNavigation('settings.email')} fallback={<Navigate to="/home/settings/tags" replace />}>
                  <React.Suspense fallback={<LoadingFallback />}>
                    <EditChannel />
                  </React.Suspense>
                </Protect>
              } />

              <Route path="tags" element={
                <Protect permission={getPermissionForNavigation('settings.tags')} fallback={<Navigate to="/home/settings/tags" replace />}>
                  <React.Suspense fallback={<LoadingFallback />}>
                    <Tags />
                  </React.Suspense>
                </Protect>
              } />

              <Route path="custom-data" element={
                <Protect permission={getPermissionForNavigation('settings.custom-data')} fallback={<Navigate to="/home/settings/tags" replace />}>
                  <React.Suspense fallback={<LoadingFallback />}>
                    <CustomData />
                  </React.Suspense>
                </Protect>
              } />

              <Route path="custom-objects" element={
                <Protect permission={getPermissionForNavigation('settings.custom-objects')} fallback={<Navigate to="/home/settings/tags" replace />}>
                  <React.Suspense fallback={<LoadingFallback />}>
                    <CustomObjects />
                  </React.Suspense>
                </Protect>
              } />

              <Route path="custom-objects/:id" element={
                <Protect permission={getPermissionForNavigation('settings.custom-objects')} fallback={<Navigate to="/home/settings/tags" replace />}>
                  <React.Suspense fallback={<LoadingFallback />}>
                    <CustomObjectDetail />
                  </React.Suspense>
                </Protect>
              } />

              <Route path="teammates" element={
                <Protect permission={getPermissionForNavigation('settings.teammates')} fallback={<Navigate to="/home/settings/tags" replace />}>
                  <React.Suspense fallback={<LoadingFallback />}>
                    <Teammates />
                  </React.Suspense>
                </Protect>
              } />

              <Route path="teammates/:id" element={
                <Protect permission={getPermissionForNavigation('settings.teammates')} fallback={<Navigate to="/home/settings/tags" replace />}>
                  <React.Suspense fallback={<LoadingFallback />}>
                    <TeammateDetail />
                  </React.Suspense>
                </Protect>
              } />

              <Route path="teams" element={
                <Protect permission={getPermissionForNavigation('settings.teams')} fallback={<Navigate to="/home/settings/tags" replace />}>
                  <React.Suspense fallback={<LoadingFallback />}>
                    <Teams />
                  </React.Suspense>
                </Protect>
              } />

              <Route path="teams/create" element={
                <Protect permission={getPermissionForNavigation('settings.teams')} fallback={<Navigate to="/home/settings/tags" replace />}>
                  <React.Suspense fallback={<LoadingFallback />}>
                    <CreateTeam />
                  </React.Suspense>
                </Protect>
              } />

              <Route path="teams/:id" element={
                <Protect permission={getPermissionForNavigation('settings.teams')} fallback={<Navigate to="/home/settings/tags" replace />}>
                  <React.Suspense fallback={<LoadingFallback />}>
                    <TeamDetail />
                  </React.Suspense>
                </Protect>
              } />

              <Route path="teams/:id/edit" element={
                <Protect permission={getPermissionForNavigation('settings.teams')} fallback={<Navigate to="/home/settings/tags" replace />}>
                  <React.Suspense fallback={<LoadingFallback />}>
                    <EditTeam />
                  </React.Suspense>
                </Protect>
              } />

              <Route path="canned-responses" element={
                <Protect permission={getPermissionForNavigation('settings.canned-responses')} fallback={<Navigate to="/home/settings/tags" replace />}>
                  <React.Suspense fallback={<LoadingFallback />}>
                    <CannedResponses />
                  </React.Suspense>
                </Protect>
              } />

              <Route path="canned-responses/create" element={
                <Protect permission={getPermissionForNavigation('settings.canned-responses')} fallback={<Navigate to="/home/settings/tags" replace />}>
                  <React.Suspense fallback={<LoadingFallback />}>
                    <CreateCannedResponse />
                  </React.Suspense>
                </Protect>
              } />

              <Route path="canned-responses/:id" element={
                <Protect permission={getPermissionForNavigation('settings.canned-responses')} fallback={<Navigate to="/home/settings/tags" replace />}>
                  <React.Suspense fallback={<LoadingFallback />}>
                    <CannedResponseDetail />
                  </React.Suspense>
                </Protect>
              } />

              <Route path="auto-reply" element={
                <Protect permission={getPermissionForNavigation('settings.auto-reply')} fallback={<Navigate to="/home/settings/tags" replace />}>
                  <React.Suspense fallback={<LoadingFallback />}>
                    {/* <AutoReplyTemp /> */}
                    <AutoReply />
                  </React.Suspense>
                </Protect>
              } />

              <Route path="autoqa" element={
                <Protect permission={getPermissionForNavigation('settings.autoqa')} fallback={<Navigate to="/home/settings/tags" replace />}>
                  <React.Suspense fallback={<LoadingFallback />}>
                    <AutoQA />
                  </React.Suspense>
                </Protect>
              } />

              <Route path="email-templates" element={
                <Protect permission={getPermissionForNavigation('settings.email-templates')} fallback={<Navigate to="/home/settings/tags" replace />}>
                  <React.Suspense fallback={<LoadingFallback />}>
                    <EmailTemplates />
                  </React.Suspense>
                </Protect>
              } />

              <Route path="autoqa/scoring-agents/:id" element={
                <Protect permission={getPermissionForNavigation('settings.autoqa')} fallback={<Navigate to="/home/settings/tags" replace />}>
                  <React.Suspense fallback={<LoadingFallback />}>
                    <ScoringAgentDetail />
                  </React.Suspense>
              //    </Protect>
              } />

              <Route path="chat" element={
                <Protect permission={getPermissionForNavigation('settings.chat')} fallback={<Navigate to="/home/settings/tags" replace />}>
                  <React.Suspense fallback={<LoadingFallback />}>
                    <Chat />
                  </React.Suspense>
                </Protect>
              } />
            </Route>
          </Route>
          {/* Not found route */}
          <Route path="*" element={
            <React.Suspense fallback={<LoadingFallback />}>
              <NotFound />
            </React.Suspense>
          } />
        </Route>
      </Routes>
    </React.Suspense >
  );
};

export default AppRoutes;